import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

type Listener = (message: Record<string, unknown>) => void;

export interface Realtime {
  connected: boolean;
  send: (message: Record<string, unknown>) => boolean;
  /** Meldet sich für Nachrichten an; Rückgabe: Abmeldung. */
  subscribe: (listener: Listener) => () => void;
}

const RealtimeContext = createContext<Realtime | null>(null);

/** Welche Abfragen bei einer Änderungsmeldung des Servers neu geladen werden. */
const TOPIC_KEYS: Record<string, string[][]> = {
  calls: [['live'], ['queues-live'], ['me-queues']],
  registrations: [['live'], ['extensions']],
  queues: [['queues-live'], ['me-queues']],
  parking: [['parking']],
  conferences: [['conferences']],
  trunks: [['trunks'], ['system-status']],
  inbox: [['voicemail'], ['voicemail-count'], ['messages']],
  history: [['calls-history']],
};

function invalidate(qc: QueryClient, topics: string[]): void {
  for (const topic of topics) for (const key of TOPIC_KEYS[topic] ?? []) void qc.invalidateQueries({ queryKey: key });
}

/** Eine WebSocket-Verbindung je Tab: Live-Aktualisierungen und Signalisierung des Browser-Telefons. */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);
  const listeners = useRef(new Set<Listener>());

  useEffect(() => {
    let closed = false;
    let retry = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let ping: ReturnType<typeof setInterval> | undefined;

    const connect = () => {
      const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/api/ws`;
      const ws = new WebSocket(url);
      socketRef.current = ws;
      ws.onopen = () => {
        retry = 0;
        setConnected(true);
        ping = setInterval(() => ws.readyState === 1 && ws.send('{"t":"ping"}'), 25_000);
        // Nach einer Unterbrechung ist alles Angezeigte womöglich veraltet.
        void qc.invalidateQueries();
      };
      ws.onmessage = (event) => {
        let msg: Record<string, unknown>;
        try {
          msg = JSON.parse(String(event.data)) as Record<string, unknown>;
        } catch {
          return;
        }
        if (msg['t'] === 'invalidate' && Array.isArray(msg['topics'])) invalidate(qc, msg['topics'] as string[]);
        for (const l of listeners.current) l(msg);
      };
      ws.onclose = (event) => {
        setConnected(false);
        if (ping) clearInterval(ping);
        socketRef.current = null;
        if (closed || event.code === 4401) return;
        retry = Math.min(retry + 1, 6);
        timer = setTimeout(connect, 500 * 2 ** retry);
      };
      ws.onerror = () => ws.close();
    };
    connect();
    return () => {
      closed = true;
      if (timer) clearTimeout(timer);
      if (ping) clearInterval(ping);
      socketRef.current?.close();
    };
  }, [qc]);

  const send = useCallback((message: Record<string, unknown>) => {
    const ws = socketRef.current;
    if (!ws || ws.readyState !== 1) return false;
    ws.send(JSON.stringify(message));
    return true;
  }, []);
  const subscribe = useCallback((listener: Listener) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);

  const value = useMemo(() => ({ connected, send, subscribe }), [connected, send, subscribe]);
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): Realtime {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error('RealtimeProvider fehlt');
  return ctx;
}
